import { createStore as vuexCreateStore } from 'vuex';

/**
 * @typedef {Object} ModalProps
 * @property {string} modalID - The ID of the modal
 * @property {function} resolve - The resolve function for the modal's promise
 * @property {function} reject - The reject function for the modal's promise
 */

/**
 * @typedef {Object} ModalItem
 * @property {string} componentName - The name of the modal component
 * @property {string} componentType - The type of the modal component
 * @property {ModalProps} props - The properties of the modal
 */

/**
 * @typedef {Object.<string, ModalItem>} ActiveModals
 */

/**
 * This function creates an instance of a Vuex store.
 * This store contains the things that all ProPoint apps require such as the logged in user, the current device,
 * the current branch, etc.
 * You can supply a parameter with additional modules to add them as modules to this store.
 *
 * This store structure is shared between all apps - epos, kitchen, kitchen-customer.
 * If you need to add something that is specific to one of the apps you should add it as a module for that app only.
 *
 * @param {{modules?:{}, state?:{}, getters?:{}, mutations?:{}}} mergeOptions Additional options to merge into the default.
 *
 * @return {Store<{}>|{builtAt: string, branch: string, commit: string}}
 */
export const createStore = (
    mergeOptions = {}
) => {
    return vuexCreateStore({
        modules: {
            ...mergeOptions.modules,
        },

        state: {
            /**
             * @type {Object<string, {componentName: String, props: ?{}}>}
             */
            activeEmbeddedComponents: {},

            /**
             * @type {{componentName: string, props: ?{}}|null}
             */
            activeFullPageComponent: null,

            /**
             * @type {ActiveModals}
             */
            activeModals: {},

            /**
             * @type {boolean}
             */
            bottomBarVisible: false,

            /**
             * @type {string}
             */
            currentPageHash: '',

            /**
             * @type {?User}
             */
            currentUser: null,

            /**
             * @type {?Branch}
             */
            currentBranch: null,

            /**
             * @type {?Branch}
             */
            currentItemBranch: null,

            /**
             * @type {?Device}
             */
            currentDevice: null,

            /**
             * A copy of the object stored in /version.json that corresponds to the installed version of ProPoint.
             *
             * @type {?{builtAt: string, branch: string, commit: string}}
             */
            eposVersionCurrent: null,

            /**
             * The latest /version.json fetched from the API.
             *
             * @type {?{builtAt: string, branch: string, commit: string}}
             */
            eposVersionLatest: null,

            /**
             * @type {boolean}
             */
            isDev: false,

            /**
             * @type {boolean}
             */
            isStaging: false,

            /**
             * @type {?boolean}
             */
            isOnline: null,

            viewPrivileges: {
                sell: false,
                redeem: false,
                accounts: false, // Formerly 'manage',
                reports: false,
                today: false,
            },

            ...mergeOptions.state,
        },

        getters: {
            loggedIn(state) {
                return !!state.currentUser;
            },

            deviceSet(state) {
                return !!(state.currentDevice && state.currentDevice.ID);
            },

            updateAvailable(state) {
                return state.eposVersionLatest
                    && JSON.stringify(state.eposVersionCurrent) !== JSON.stringify(state.eposVersionLatest);
            },

            eposVersionCurrent(state) {
                return state.eposVersionCurrent;
            },

            eposVersionLatest(state) {
                return state.eposVersionLatest;
            },

            activeEmbeddedComponents(state) {
                const { activeEmbeddedComponents } = state;

                return activeEmbeddedComponents && Object.keys(activeEmbeddedComponents).length > 0
                    ? activeEmbeddedComponents
                    : {};
            },

            /**
             * Returns the current active modal type modals (the default type).
             *
             * @param state
             *
             * @return {ActiveModals|null}
             */
            activeModals(state) {
                const { activeModals } = state;
                return activeModals && Object.keys(activeModals).length > 0 ? activeModals : null;
            },

            ...mergeOptions.getters,
        },

        mutations: {
            addActiveEmbeddedComponent(state, value) {
                state.activeEmbeddedComponents[value.name] = value;
            },

            removeActiveEmbeddedComponent(state, name) {
                delete state.activeEmbeddedComponents[name];
            },

            setActiveFullPageComponent(state, value) {
                state.activeFullPageComponent = value;
            },

            addModalToStack(state, { modalID, value }) {
                state.activeModals[modalID] = value;
            },

            removeModalFromStack(state, modalID) {
                // If we just remove the modal from the list it won't transition out.
                // We use v-if on the modal in OverlayStack to cause it to transition out too.
                // Set the trigger to true so that the transition will happen.
                state.activeModals[modalID].leaving = true;

                // After the transition is done we can remove the modal from the list.
                setTimeout(() => {
                    delete state.activeModals[modalID];
                }, 1000);
            },

            setBottomBarVisible(state, value) {
                state.bottomBarVisible = value;
            },

            /**
             * @param {{}} state
             * @param {string} value
             */
            setCurrentPageHash(state, value) {
                state.currentPageHash = value;
            },

            /**
             * @param {{}} state
             * @param {?User} user
             */
            setCurrentUser(state, user) {
                state.currentUser = user;
            },

            /**
             * @param {{}} state
             * @param {?Branch} branch
             */
            setCurrentBranch(state, branch) {
                state.currentBranch = branch;
            },

            /**
             * @param {{}} state
             * @param {?Branch} branch
             */
            setCurrentItemBranch(state, branch) {
                state.currentItemBranch = branch;
            },

            /**
             * @param {{}} state
             * @param {?Device} device
             */
            setCurrentDevice(state, device) {
                state.currentDevice = device;
            },

            /**
             * @param {{}} state
             * @param {{builtAt: string, branch: string, commit: string}} versionJson
             */
            setEposVersionCurrent(state, versionJson) {
                state.eposVersionCurrent = versionJson;
            },

            /**
             * @param {{}} state
             * @param {{builtAt: string, branch: string, commit: string}} versionJson
             */
            setEposVersionLatest(state, versionJson) {
                state.eposVersionLatest = versionJson;
            },

            /**
             * @param {{}} state
             * @param {?boolean} isDev
             */
            setIsDev(state, isDev) {
                state.isDev = isDev;
            },

            /**
             * @param {{}} state
             * @param {?boolean} isStaging
             */
            setIsStaging(state, isStaging) {
                state.isStaging = isStaging;
            },

            /**
             * @param {{}} state
             * @param {?boolean} isOnline
             */
            setIsOnline(state, isOnline) {
                state.isOnline = isOnline;
            },

            /**
             * @param {{}} state
             * @param {{viewName: string, value: boolean}} data
             */
            setViewPrivilege(state, { viewName, value }) {
                state.viewPrivileges[viewName] = value;
            },

            ...mergeOptions.mutations,
        },
    });
};
